import { useMotionValue, animate } from 'motion/react';
import { useEffect, useRef } from 'react';

type UseSwipeAnimationProps<T> = {
  profile?: T;
  onLike?: (item: T) => void;
  onSkip?: (item: T) => void;
};

const SWIPE_THRESHOLD = 100;
const OPACITY_THRESHOLD = SWIPE_THRESHOLD * 1.5;
const ROTATION_FACTOR = 30;
const SWIPE_DISTANCE = 500;
const SWIPE_ACTIVATION_THRESHOLD = 10;

export const useSwipeAnimation = <T>({
  profile,
  onLike,
  onSkip,
}: UseSwipeAnimationProps<T>) => {
  const likeGradientRef = useRef<HTMLDivElement | null>(null);
  const dislikeGradientRef = useRef<HTMLDivElement | null>(null);

  const x = useMotionValue(0);
  const rotate = useMotionValue(0);
  const opacity = useMotionValue(1);

  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    const viewport = window.visualViewport;

    if (!meta || !(meta instanceof HTMLMetaElement) || !viewport) return;

    const allowZoom = (isAllow: boolean) => {
      meta.content = isAllow
        ? 'width=device-width, initial-scale=1.0, viewport-fit=cover'
        : 'width=device-width, initial-scale=1.0, maximum-scale=1, user-scalable=0, viewport-fit=cover';
    };

    allowZoom(true);

    const onResize = () => {
      if (viewport.scale > 1) {
        allowZoom(false);
        requestAnimationFrame(() => allowZoom(true));
      }
    };

    viewport.addEventListener('resize', onResize);

    return () => {
      viewport.removeEventListener('resize', onResize);
      allowZoom(false);
    };
  }, []);

  const setGradient = (direction: 'left' | 'right', opacity: number) => {
    const showRef = direction === 'left' ? dislikeGradientRef : likeGradientRef;
    const hideRef = direction === 'left' ? likeGradientRef : dislikeGradientRef;

    if (showRef.current) showRef.current.style.opacity = `${opacity}`;
    if (hideRef.current) hideRef.current.style.opacity = '0';
  };

  const handleDrag = (
    _: MouseEvent | TouchEvent,
    info: { offset: { x: number } },
  ) => {
    const offsetX = info.offset.x;

    x.set(offsetX);
    rotate.set(offsetX / ROTATION_FACTOR);

    const opacity = Math.min(Math.abs(info.offset.x) / OPACITY_THRESHOLD, 1);

    setGradient(offsetX > 0 ? 'right' : 'left', opacity);

    if (Math.abs(offsetX) > SWIPE_ACTIVATION_THRESHOLD) {
      const root = document.getElementById('root');
      if (root) {
        root.style.overflowY = 'hidden';
      }
    }
  };

  const handleDragEnd = async (
    _: MouseEvent | TouchEvent,
    info: { offset: { x: number } },
  ) => {
    const root = document.getElementById('root');
    if (root) {
      root.style.overflowY = 'scroll';
    }

    if (info.offset.x > SWIPE_THRESHOLD) {
      await Promise.all([
        animate(x, SWIPE_DISTANCE, { duration: 0.25 }),
        animate(rotate, 30, { duration: 0.25 }),
        animate(opacity, 0, { duration: 0.25 }),
      ]);
      profile && onLike?.(profile);
    } else if (info.offset.x < -SWIPE_THRESHOLD) {
      await Promise.all([
        animate(x, -SWIPE_DISTANCE, { duration: 0.25 }),
        animate(rotate, -30, { duration: 0.25 }),
        animate(opacity, 0, { duration: 0.25 }),
      ]);
      profile && onSkip?.(profile);
    } else {
      await Promise.all([
        animate(x, 0, { duration: 0.25 }),
        animate(rotate, 0, { duration: 0.25 }),
        animate(opacity, 1, { duration: 0.25 }),
      ]);
      setGradient('left', 0);
      setGradient('right', 0);
    }
  };

  /** A tap on a feed button (board A.3): the wash and icon, then it leaves. */
  const flyOut = async (direction: 'left' | 'right') => {
    const isRight = direction === 'right';
    const wash = isRight ? likeGradientRef.current : dislikeGradientRef.current;
    const flight = { duration: 0.4, delay: 0.24, ease: 'easeIn' } as const;

    await Promise.all([
      wash &&
        animate(wash, { opacity: 1 }, { duration: 0.18, ease: 'easeOut' }),
      animate(x, isRight ? SWIPE_DISTANCE : -SWIPE_DISTANCE, flight),
      animate(rotate, isRight ? 16 : -16, flight),
    ]);

    if (!profile) return;

    if (isRight) onLike?.(profile);
    else onSkip?.(profile);
  };

  return {
    likeGradientRef,
    dislikeGradientRef,
    handleDragEnd,
    handleDrag,
    flyOut,
    x,
    rotate,
    opacity,
  };
};
